import { useContext, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { AssemblyPricingDisplay } from './components/AssemblyPricingDisplay';
import { useParams } from 'react-router-dom';
import { useQuoteState } from '../../../../stateProviders/QuoteStateProvider';
import { DeckContext } from '../../views/blocks/context/DeckContext';
import { DeckContext as DeckContextV3 } from '../../views/blocks/context/DeckContextV3';
import { QuoteState } from '../../../../types/stateProviders/QuoteStateProvider';
import { LoadingSpinner } from '../../../../globalComponents/LoadingSpinner';
import React from 'react';
import removeBlockRefs from '../../../../utils/removeBlockRefs';
import { useGlobalState } from 'src/stateProviders/GlobalStateProvider';
import useIsBetaCanvasEnabled from 'src/hooks/useBetaCanvas';
import useIsDesignPackageEnabled from 'src/hooks/useDesignPackageEnabled';

export const MaterialCountsCosts = () => {
	const sidebarRef = useRef<HTMLDivElement>(null);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(0); // Tab state to track the selected option
	const [pricingOptions, setPricingOptions] = useState(null); // State to store the pricing data for all options
	const api = useApi();
	const { quoteId } = useParams();
	const quoteState = useQuoteState();
	const globalState = useGlobalState();
	const isBetaEnabled = useIsBetaCanvasEnabled();
	const isDesignPackageEnabled = useIsDesignPackageEnabled();
	const { state } = useContext(isBetaEnabled ? DeckContextV3 : DeckContext);
	const [apiError, setApiError] = useState({
		errored: false,
		error: {
			message: '',
		},
		errorType: '',
	});
	const [loading, setLoading] = useState(false);

	// Fetch selected option's configuration
	const selectedOption = quoteState.options[activeTab];

	// Design package cost calculation
	const DESIGN_PACKAGE_PRICES: Record<string, number> = {
		'Basic Framing Plan': 880,
		'Bronze Package': 5310,
		'Silver Package': 9900,
		'Gold Package': 10450,
	};
	const designPackageName = quoteState.designPackage;
	const designPackageBase = designPackageName
		? (DESIGN_PACKAGE_PRICES[designPackageName] ?? 0)
		: 0;
	const designPackageNoPermit =
		quoteState.permitRequired === false &&
		designPackageName !== null &&
		['Bronze Package', 'Silver Package', 'Gold Package'].includes(
			designPackageName ?? ''
		);
	const designPackageNet = designPackageNoPermit
		? designPackageBase - 2310
		: designPackageBase;

	useEffect(() => {
		if (!quoteId) return;

		const cleanedOptions: QuoteState['options'] = quoteState.options.filter(
			(option) => {
				return option.deckingType && option.deckingType.length;
			}
		);

		if (cleanedOptions.length === 0) {
			setApiError({
				errored: true,
				error: {
					message: 'Make sure to have at least a decking type selected.',
				},
				errorType: 'options',
			});
			setLoading(false);
			return;
		}

		// Single API call to fetch all pricing options on component mount or relevant state changes
		(async () => {
			setLoading(true);

			let response;

			if (isBetaEnabled) {
				response = await api.calculatePricingV3.post(
					quoteId,
					cleanedOptions, // Pass all options from currentQuote
					removeBlockRefs(state.blocks)
				);
			} else {
				response = await api.calculatePricing.post(
					quoteId,
					cleanedOptions, // Pass all options from currentQuote
					removeBlockRefs(state.blocks)
				);
			}

			if (response.error) {
				setApiError(response);
				setLoading(false);
				return;
			}

			// Store the pricing data for each option
			setPricingOptions(response);
			setApiError({
				errored: false,
				error: {
					message: '',
				},
				errorType: '',
			});
			setLoading(false);
		})();
	}, [quoteId, quoteState.options]);

	// Get the current tab’s pricing data
	const optionKey = `option${activeTab + 1}`;
	const optionPricing: any = pricingOptions?.[optionKey];

	return (
		<>
			<button
				style={{
					right: sidebarOpen ? sidebarRef.current?.offsetWidth : 0,
					zIndex: 1001,
				}}
				onClick={() => setSidebarOpen(!sidebarOpen)}
				className="fixed h-full w-[30px] bg-primary hover:bg-primaryHover transition-[0.2s]"
			>
				<svg
					className={`${sidebarOpen ? '' : 'scale-[-1]'} transition`}
					height="24px"
					viewBox="0 -960 960 960"
					width="24px"
					fill="white"
				>
					<path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
				</svg>
			</button>
			<div
				style={{
					width: sidebarOpen ? sidebarRef.current?.offsetWidth : 0,
					zIndex: 10001,
				}}
				className="bg-white h-full fixed right-0 transition-all overflow-hidden"
			>
				<div
					ref={sidebarRef}
					className="w-[400px] flex flex-col gap-[20px] pt-[15px] overflow-y-scroll h-full"
				>
					<div className="px-[15px]">
						<h2 className="font-bold text-[24px]">Material Counts and Costs</h2>
					</div>
					<ul className="flex">
						{['Option 1', 'Option 2', 'Option 3'].map((label, index) => (
							<li
								key={index}
								className="flex-1 border border-black text-center"
							>
								<button
									className={`w-full ${activeTab === index ? 'bg-primary text-white' : ''}`}
									onClick={() => setActiveTab(index)}
								>
									{label}
								</button>
							</li>
						))}
					</ul>

					{/* Display selected option's details */}
					<div className="px-[15px] flex flex-col gap-[5px] mt-[10px] border-b pb-[10px]">
						<h3 className="font-bold">
							Selections for {`Option ${activeTab + 1}`}
						</h3>
						<p>
							<strong>Decking Type:</strong>{' '}
							{selectedOption.deckingType || 'Not selected'}
						</p>
						<p>
							<strong>Railing Type:</strong>{' '}
							{selectedOption.railingType || 'Not selected'}
						</p>
						{selectedOption.railCapStyle && (
							<p>
								<strong>Rail Cap Style:</strong>{' '}
								{selectedOption.railCapStyle || 'Not selected'}
							</p>
						)}
					</div>

					{loading && <LoadingSpinner />}

					{apiError.errored && !loading && (
						<div className="w-full flex flex-col p-5">
							<h2 className="text-red-500 text-[32px]">API Error:</h2>
							<p className="text-red-500">{apiError.error.message}</p>
						</div>
					)}
					{/* Pricing display for selected option */}

					{!apiError.errored && !loading && (
						<div className="px-[15px] flex flex-col gap-[10px] mt-[10px] pb-[200px]">
							{/* Decking */}
							<AssemblyPricingDisplay
								assemblyType="Decking Deckboards"
								assemblyMaterials={optionPricing?.deckboardMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Hanger Nails"
								assemblyMaterials={optionPricing?.deckingHangerNailsMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Screws"
								assemblyMaterials={optionPricing?.deckingScrewsMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Labor"
								assemblyMaterials={optionPricing?.deckingLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Border and Spline"
								assemblyMaterials={optionPricing?.deckingBorderSplineMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Border and Spline Labor"
								assemblyMaterials={
									optionPricing?.deckingBorderSplineLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Ledger Band"
								assemblyMaterials={optionPricing?.deckingLedgerbandMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Ledger Bolt"
								assemblyMaterials={optionPricing?.deckingLedgerBoltMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Outer Band"
								assemblyMaterials={optionPricing?.deckingOuterbandMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Posts"
								assemblyMaterials={optionPricing?.deckingPostMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Post Labor"
								assemblyMaterials={optionPricing?.deckingPostLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Post Footings"
								assemblyMaterials={optionPricing?.deckingFootingMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Post Footing Labor"
								assemblyMaterials={
									optionPricing?.deckingPostFootingLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Joists"
								assemblyMaterials={optionPricing?.deckingJoistAssemblyMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Flashing"
								assemblyMaterials={optionPricing?.deckingFlashingMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Side Band"
								assemblyMaterials={optionPricing?.deckingSidebandMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Guardrail"
								assemblyMaterials={optionPricing?.deckingGuardrailMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Guardrail Post"
								assemblyMaterials={optionPricing?.deckingGuardrailPostMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Guardrail Cap"
								assemblyMaterials={optionPricing?.deckingGuardrailCapMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Guardrail Labor"
								assemblyMaterials={optionPricing?.deckingRailingLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Guardrail Cap Labor"
								assemblyMaterials={
									optionPricing?.deckingGuardrailCapLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Fascia"
								assemblyMaterials={optionPricing?.deckingFasciaMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Decking Fascia Labor"
								assemblyMaterials={optionPricing?.deckingFasciaLaborMaterials}
							/>
							{optionPricing?.dropbeamMaterials && (
								<AssemblyPricingDisplay
									assemblyType="Decking Dropbeam"
									assemblyMaterials={optionPricing?.dropbeamMaterials}
								/>
							)}
							<AssemblyPricingDisplay
								assemblyType="Decking/Landing Clips"
								assemblyMaterials={
									optionPricing?.deckingAndLandingClipsMaterials
								}
							/>
							{/* Landing */}
							<AssemblyPricingDisplay
								assemblyType="Landing Deckboards"
								assemblyMaterials={optionPricing?.landingDeckBoardMaterials}
							/>{' '}
							<AssemblyPricingDisplay
								assemblyType="Landing Hanger Nails"
								assemblyMaterials={optionPricing?.landingHangerNailsMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Screws"
								assemblyMaterials={optionPricing?.landingDeckingScrewsMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Labor"
								assemblyMaterials={optionPricing?.landingLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Border and Spline"
								assemblyMaterials={optionPricing?.landingBorderSplineMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Border and Spline Labor"
								assemblyMaterials={
									optionPricing?.landingBorderSplineLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Ledger Band"
								assemblyMaterials={optionPricing?.landingLedgerbandMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Ledger Bolt"
								assemblyMaterials={optionPricing?.landingLedgerBoltMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Outer Band"
								assemblyMaterials={optionPricing?.landingOuterbandMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Posts"
								assemblyMaterials={optionPricing?.landingPostMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Post Labor"
								assemblyMaterials={optionPricing?.landingPostLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Post Footings"
								assemblyMaterials={optionPricing?.landingFootingMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Post Footing Labor"
								assemblyMaterials={
									optionPricing?.landingPostFootingLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Joists"
								assemblyMaterials={optionPricing?.landingJoistAssemblyMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Flashing"
								assemblyMaterials={optionPricing?.landingFlashingMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Side Band"
								assemblyMaterials={optionPricing?.landingSidebandMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Guardrail"
								assemblyMaterials={optionPricing?.landingGuardrailMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Guardrail Post"
								assemblyMaterials={optionPricing?.landingGuardrailPostMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Guardrail Cap"
								assemblyMaterials={optionPricing?.landingGuardrailCapMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Guardrail Labor"
								assemblyMaterials={optionPricing?.landingRailingLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Guardrail Cap Labor"
								assemblyMaterials={
									optionPricing?.landingGuardrailCapLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Fascia"
								assemblyMaterials={optionPricing?.landingFasciaMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Landing Fascia Labor"
								assemblyMaterials={optionPricing?.landingFasciaLaborMaterials}
							/>
							{/* Bumpout */}
							<AssemblyPricingDisplay
								assemblyType="Bumpout"
								assemblyMaterials={optionPricing?.bumpoutMaterials}
							/>
							{/* Stairs */}
							<AssemblyPricingDisplay
								assemblyType="Stair Rail"
								assemblyMaterials={optionPricing?.stairRailMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Rail Cap"
								assemblyMaterials={optionPricing?.stairRailCapMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Rail Post"
								assemblyMaterials={optionPricing?.stairRailPostMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Guardrail Labor"
								assemblyMaterials={optionPricing?.stairRailingLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Guardrail Cap Labor"
								assemblyMaterials={
									optionPricing?.stairGuardrailCapLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Stringers"
								assemblyMaterials={optionPricing?.stairStringerMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Risers"
								assemblyMaterials={optionPricing?.stairRiserMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Posts"
								assemblyMaterials={optionPricing?.stairPostMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Post Footings"
								assemblyMaterials={optionPricing?.stairPostFootingMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Treads"
								assemblyMaterials={optionPricing?.stairTreadMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Labor"
								assemblyMaterials={optionPricing?.stairLabor}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Post Labor"
								assemblyMaterials={optionPricing?.stairPostLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stair Post Footing Labor"
								assemblyMaterials={
									optionPricing?.stairPostFootingLaborMaterials
								}
							/>
							<AssemblyPricingDisplay
								assemblyType="Graspable Handrail"
								assemblyMaterials={optionPricing?.graspableHandrailMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stairs Fascia"
								assemblyMaterials={optionPricing?.stairFasciaMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Stairs Fascia Labor"
								assemblyMaterials={optionPricing?.stairFasciaLaborMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Fascia Screws"
								assemblyMaterials={optionPricing?.fasciaScrewsMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Fascia Drill Bit"
								assemblyMaterials={optionPricing?.fasciaDrillBitMaterials}
							/>
							<AssemblyPricingDisplay
								assemblyType="Accessories"
								assemblyMaterials={optionPricing?.accessoryMaterials}
							/>
							{isDesignPackageEnabled && designPackageName && (
								<div className="mt-[10px] border-t pt-[10px]">
									<h3 className="font-bold text-[18px]">Design Package</h3>
									<div className="grid grid-cols-2 border border-[lightgrey]">
										<div className="border-b px-[5px] border-r font-semibold">
											Package
										</div>
										<div className="border-b px-[5px] font-semibold">Cost</div>
										<div className="px-[5px] border-r text-[12px]">
											{designPackageName}
											{designPackageNoPermit && (
												<span className="ml-1 text-green-600 text-[11px]">
													(No permit discount applied)
												</span>
											)}
										</div>
										<div className="px-[5px] text-[12px]">
											$
											{designPackageNet.toLocaleString('en-US', {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}
										</div>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	);
};
